import { useCallback, useState } from 'react';
import {
  AnnouncementPlacement,
  PublicAnnouncement,
} from '@/api/app-state/app-state.types.ts';
import { useAppState } from '@/api/app-state/app-state.hooks.ts';

const DISMISS_PREFIX = 'eos-ann-dismissed:';

const dismissKey = (id: string) => `${DISMISS_PREFIX}${id}`;

const isDismissed = (id: string): boolean => {
  try {
    return localStorage.getItem(dismissKey(id)) === '1';
  } catch {
    return false;
  }
};

/**
 * Centralizes the filter-by-surface + dismissed-memory logic so the three
 * shells stay DRY. An announcement matches a surface when its `placement`
 * includes that surface or `GLOBAL`. Dismissals are remembered in localStorage
 * keyed by announcement id, so re-publishing under a new id re-shows it.
 */
export const useEffectiveAnnouncements = (
  placement: AnnouncementPlacement,
): {
  banners: PublicAnnouncement[];
  popups: PublicAnnouncement[];
  dismiss: (id: string) => void;
} => {
  const { data } = useAppState();
  // Bump on dismiss so the memoless filter below re-runs.
  const [, force] = useState(0);

  const dismiss = useCallback((id: string) => {
    try {
      localStorage.setItem(dismissKey(id), '1');
    } catch {
      // ignore storage failures
    }
    force((n) => n + 1);
  }, []);

  const matches = (a: PublicAnnouncement) =>
    a.placement.includes(placement) || a.placement.includes('GLOBAL');

  const visible = (data?.announcements ?? [])
    .filter(matches)
    .filter((a) => !isDismissed(a.id));

  const banners = visible.filter(
    (a) => a.displayMode === 'BANNER' || a.displayMode === 'BOTH',
  );
  const popups = visible.filter(
    (a) => a.displayMode === 'POPUP' || a.displayMode === 'BOTH',
  );

  return { banners, popups, dismiss };
};
